import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Calendar, Eye, Tag, Newspaper, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { NSBLogo } from "../components/NSBLogo";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DIVISIONS = ["Inside NSB", "High School", "BC9 HBCU", "D1", "D2", "D3", "NAIA", "JUCO"];

const DIVISION_COLORS = {
  "Inside NSB": { border: "border-white/40", bg: "bg-white/10", text: "text-white/80", activeBg: "bg-white/20" },
  "High School": { border: "border-orange-500/40", bg: "bg-orange-500/10", text: "text-orange-400", activeBg: "bg-orange-600" },
  "BC9 HBCU": { border: "border-teal-500/40", bg: "bg-teal-500/10", text: "text-teal-400", activeBg: "bg-teal-600" },
  "D1": { border: "border-red-500/40", bg: "bg-red-500/10", text: "text-red-400", activeBg: "bg-red-600" },
  "D2": { border: "border-blue-500/40", bg: "bg-blue-500/10", text: "text-blue-400", activeBg: "bg-blue-600" },
  "D3": { border: "border-amber-500/40", bg: "bg-amber-500/10", text: "text-amber-400", activeBg: "bg-amber-600" },
  "NAIA": { border: "border-green-500/40", bg: "bg-green-500/10", text: "text-green-400", activeBg: "bg-green-600" },
  "JUCO": { border: "border-purple-500/40", bg: "bg-purple-500/10", text: "text-purple-400", activeBg: "bg-purple-600" },
};

export default function NewsDeskPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDiv = searchParams.get("division") || "D1";
  const [activeDivision, setActiveDivision] = useState(initialDiv);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/blog/posts?division=${encodeURIComponent(activeDivision)}&limit=30`)
      .then(r => r.json())
      .then(d => setPosts(d.posts || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [activeDivision]);

  const handleDivisionChange = (div) => {
    setActiveDivision(div);
    setSearchParams({ division: div });
  };

  const colors = DIVISION_COLORS[activeDivision] || DIVISION_COLORS["D1"];

  return (
    <div className="min-h-screen bg-[#020817] text-white" data-testid="newsdesk-page">
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#020817] via-[#0a0e27] to-black" />
      </div>

      {/* Nav */}
      <nav className="relative z-20 border-b border-white/5 backdrop-blur-xl bg-black/40">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <NSBLogo className="h-10" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5 border border-white/10">
              <Newspaper className="w-4 h-4 text-red-400" />
              <span className="text-xs font-bold text-white/70 tracking-wider uppercase">NSB ScoutWire News</span>
            </div>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-white/60 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" /> Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="relative z-10 max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <div className="mb-8">
          <span className="text-xs tracking-[0.4em] text-red-400/80 uppercase">NSB Intelligence</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black mt-2 tracking-tight" data-testid="newsdesk-title">NSB ScoutWire News</h1>
          <p className="text-sm text-white/40 mt-2">Breaking news, recruiting intel, and analysis across every division of college baseball.</p>
        </div>

        {/* Division Tabs */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="division-tabs">
          {DIVISIONS.map((div) => {
            const dc = DIVISION_COLORS[div];
            const isActive = activeDivision === div;
            return (
              <button
                key={div}
                onClick={() => handleDivisionChange(div)}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                  isActive
                    ? `${dc.activeBg} text-white shadow-lg`
                    : `bg-white/5 border ${dc.border} ${dc.text} hover:bg-white/10`
                }`}
                data-testid={`division-tab-${div.toLowerCase().replace(/\s/g, '-')}`}
              >
                {div}
              </button>
            );
          })}
        </div>

        {/* Articles */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <Newspaper className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/40">No articles yet for {activeDivision}. Check back soon.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="newsdesk-articles">
            {posts.map((post, i) => (
              <Link
                key={post.post_id}
                to={`/blog/${post.slug || post.post_id}`}
                className={`block rounded-xl border ${colors.border} bg-gradient-to-r from-white/[0.03] to-transparent p-5 sm:p-6 hover:bg-white/[0.06] transition-all group`}
                data-testid={`news-article-${i}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex gap-3 flex-1 min-w-0">
                    {/* Thumbnail */}
                    {post.featured_image_url && (
                      <div className="w-24 h-16 sm:w-32 sm:h-20 rounded-lg overflow-hidden flex-shrink-0 bg-white/5">
                        <img src={post.featured_image_url} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${colors.bg} ${colors.text}`}>
                        {post.division}
                      </span>
                      {post.category && post.category !== "news" && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-white/5 text-white/40">
                          {post.category}
                        </span>
                      )}
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-white group-hover:text-red-400 transition mb-1 leading-snug">{post.title}</h3>
                    <p className="text-sm text-white/50 leading-relaxed line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-white/30">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                      {post.author_name && <span>{post.author_name}</span>}
                      {post.views > 0 && <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {post.views}</span>}
                    </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-red-400 transition flex-shrink-0 mt-1" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
